import { html } from '../../html.js';
import type { VNode } from '../../html.js';
import { Empty, Scroller } from './bits.js';

/**
 * The two schemas, read as tables an operator can check against reality.
 *
 * `inputSchema` is what an agent is told it may pass and `outputSchema` is what
 * it is promised back. Both are shown in full — types, which fields are required,
 * the enum members — because the manifest's most expensive defects live exactly
 * there: a required parameter the binding never reads, or an output contract that
 * names fields the pipeline does not emit. A card that printed
 * `get_film_details(film)` and stopped would hide both.
 *
 * `outputSchemaFor` in @clawharbor/manifest derives the output contract from the
 * binding itself, so what is rendered here is not aspirational: it is the wire
 * shape the pipeline can actually produce.
 */

export interface SchemaField {
  name: string;
  type: string;
  required: boolean;
  enumValues: string[] | null;
  description: string | null;
  fallback: unknown;
  minimum: number | null;
  maximum: number | null;
}

function typeName(node: Record<string, unknown>): string {
  const t = node['type'];
  if (typeof t === 'string') return t;
  if (Array.isArray(t)) return t.filter((x) => typeof x === 'string').join(' | ') || 'unknown';
  if (Array.isArray(node['enum'])) return 'enum';
  return 'unknown';
}

export function fieldsOf(schema: unknown): SchemaField[] {
  if (schema == null || typeof schema !== 'object') return [];
  const s = schema as Record<string, unknown>;
  const props = s['properties'];
  if (props == null || typeof props !== 'object') return [];
  const required = new Set(
    Array.isArray(s['required']) ? s['required'].filter((r): r is string => typeof r === 'string') : [],
  );

  return Object.entries(props as Record<string, unknown>).map(([name, raw]) => {
    const node = raw != null && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
    const values = Array.isArray(node['enum'])
      ? node['enum'].map((v) => String(v))
      : null;
    return {
      name,
      type: typeName(node),
      required: required.has(name),
      enumValues: values && values.length ? values : null,
      description: typeof node['description'] === 'string' ? node['description'] : null,
      fallback: node['default'],
      minimum: typeof node['minimum'] === 'number' ? node['minimum'] : null,
      maximum: typeof node['maximum'] === 'number' ? node['maximum'] : null,
    };
  });
}

/**
 * String field values from the form, coerced to the types the schema declares.
 *
 * An integer parameter sent as `"3"` is a different call from one sent as `3`,
 * and the binding runtime's `limit` op takes a number. An empty optional field is
 * OMITTED rather than sent as an empty string, because `skipIfEmpty` treats those
 * differently and "I left it blank" must mean "I did not pass it".
 */
export function coerceArgs(
  fields: SchemaField[],
  raw: Record<string, string>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    const value = raw[f.name];
    if (value === undefined || value === '') continue;
    if (f.type === 'integer') {
      const n = Number.parseInt(value, 10);
      if (Number.isFinite(n)) out[f.name] = n;
      continue;
    }
    if (f.type === 'number') {
      const n = Number(value);
      if (Number.isFinite(n)) out[f.name] = n;
      continue;
    }
    if (f.type === 'boolean') {
      out[f.name] = value === 'true';
      continue;
    }
    out[f.name] = value;
  }
  return out;
}

/** Required fields that were left blank, named so the message can list them. */
export function missingRequired(
  fields: SchemaField[],
  raw: Record<string, string>,
): string[] {
  return fields.filter((f) => f.required && !raw[f.name]).map((f) => f.name);
}

export function InputTable(props: { fields: SchemaField[] }): VNode {
  if (!props.fields.length) {
    return Empty({
      title: 'No input parameters.',
      body: 'This tool takes no arguments: every call returns the same answer, whoever asks and however they phrase it.',
    });
  }
  return Scroller({
    label: 'Input parameters',
    children: html`
      <table class="ptable">
        <thead>
          <tr>
            <th scope="col">Parameter</th>
            <th scope="col">Type</th>
            <th scope="col">Required</th>
            <th scope="col">Accepted values</th>
          </tr>
        </thead>
        <tbody>
          ${props.fields.map(
            (f) => html`
              <tr>
                <td><code>${f.name}</code></td>
                <td class="ptable__type">${f.type}</td>
                <td>
                  ${f.required
                    ? html`<span class="req">required</span>`
                    : html`<span class="opt">optional</span>`}
                </td>
                <td>
                  ${f.enumValues
                    ? html`<span class="ptable__enum">${f.enumValues.join(' · ')}</span>`
                    : f.minimum !== null || f.maximum !== null
                      ? html`<span class="ptable__enum">
                          ${f.minimum ?? '−∞'} … ${f.maximum ?? '∞'}
                        </span>`
                      : html`<span class="ptable__any">any ${f.type}</span>`}
                  ${f.description ? html`<p class="ptable__desc">${f.description}</p>` : null}
                </td>
              </tr>
            `,
          )}
        </tbody>
      </table>
    `,
  });
}

export function OutputFields(props: { schema: unknown }): VNode {
  const fields = fieldsOf(props.schema);
  if (!fields.length) {
    return Empty({
      title: 'No output schema.',
      body: 'Agents are told nothing about the shape of the rows this tool returns, so they have to infer it from the first answer they get. Publishing one is a manifest edit, not a dashboard one.',
    });
  }
  return html`
    <ul class="ofields">
      ${fields.map(
        (f) => html`<li class="ofield">
          <code>${f.name}</code><span class="ofield__type">${f.type}</span>
        </li>`,
      )}
    </ul>
  `;
}
